import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { api, apiError, formatPhone } from "@/lib/api";
import { toast } from "sonner";
import { UserCog, ShieldCheck, Mail, Phone, MapPin, Megaphone, X, Loader2, Send, Users, LayoutGrid, List as ListIcon, Eye, Maximize2, Search, ChevronDown, Check } from "lucide-react";
import AgentToolkit from "@/components/AgentToolkit";

export default function ClassAgents() {
  const [agents, setAgents] = useState([]);
  const [emailable, setEmailable] = useState(0);
  const [loading, setLoading] = useState(true);
  const [compose, setCompose] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("ud_agents_view") || "grid");
  const [previewYear, setPreviewYear] = useState(() => localStorage.getItem("ud_agents_preview_year") || "");
  const setPreviewYearPersist = (y) => { setPreviewYear(y); localStorage.setItem("ud_agents_preview_year", y || ""); };
  const [target, setTarget] = useState(40);
  const [savingTarget, setSavingTarget] = useState(false);
  const [tkKey, setTkKey] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedClasses, setSelectedClasses] = useState([]); // [] = all classes
  const [classMenuOpen, setClassMenuOpen] = useState(false);

  const setViewPersist = (v) => { setView(v); localStorage.setItem("ud_agents_view", v); };

  const load = () => {
    setLoading(true);
    api.get("/class-agents")
      .then((r) => { setAgents(r.data.agents || []); setEmailable(r.data.emailable || 0); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const byYear = useMemo(() => {
    const groups = {};
    for (const a of agents) {
      const y = a.class_year || "—";
      (groups[y] = groups[y] || []).push(a);
    }
    return Object.entries(groups).sort((a, b) => (Number(b[0]) || 0) - (Number(a[0]) || 0));
  }, [agents]);

  const filteredAgents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return agents.filter((a) => {
      if (selectedClasses.length && !selectedClasses.includes(String(a.class_year || "—"))) return false;
      if (!q) return true;
      const hay = [a.name, a.email, a.city, a.state, a.class_year, a.phone].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [agents, search, selectedClasses]);

  const shownByYear = useMemo(() => {
    const groups = {};
    for (const a of filteredAgents) {
      const y = a.class_year || "—";
      (groups[y] = groups[y] || []).push(a);
    }
    return Object.entries(groups).sort((a, b) => (Number(b[0]) || 0) - (Number(a[0]) || 0));
  }, [filteredAgents]);

  return (
    <div className="fade-up">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Class Agents &amp; Volunteers</div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-1">Class Agents</h1>
          <p className="text-sm text-muted-foreground mt-1">Designated volunteers who steward their graduating class.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="message-agents-btn" onClick={() => setCompose(true)} disabled={!agents.length}
            className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-secondary text-white text-sm font-medium hover:brightness-110 disabled:opacity-50">
            <Megaphone className="h-4 w-4" /> Message All Agents
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-stretch gap-3 mb-8">
        <Stat label="Class Agents" value={agents.length} icon={UserCog} />
        <Stat label="Classes Covered" value={byYear.length} icon={ShieldCheck} />
        <Stat label="Emailable" value={emailable} icon={Mail} />
      </div>

      {agents.length > 0 && (
        <div className="mb-8" data-testid="admin-toolkit-preview">
          <div className="flex items-center gap-2 mb-3 flex-wrap text-xs font-mono uppercase tracking-widest text-muted-foreground">
            <Eye className="h-3.5 w-3.5" /> Agent Toolkit Preview
            <select data-testid="preview-year-select" value={previewYear || byYear[0]?.[0] || ""} onChange={(e) => setPreviewYearPersist(e.target.value)}
              className="ml-1 rounded-md border border-input bg-background px-2 py-1 text-xs normal-case tracking-normal">
              {byYear.map(([y]) => <option key={y} value={y}>Class of {y}</option>)}
            </select>
            <span className="mx-1 opacity-40">·</span>
            <span className="normal-case tracking-normal">Participation goal</span>
            <input data-testid="target-input" type="number" min="1" max="100" value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-16 rounded-md border border-input bg-background px-2 py-1 text-xs normal-case tracking-normal" />
            <span className="normal-case tracking-normal">%</span>
            <button data-testid="save-target" disabled={savingTarget}
              onClick={async () => {
                const y = previewYear || byYear[0]?.[0];
                setSavingTarget(true);
                try {
                  await api.put(`/class-targets/${y}`, { target: Number(target) });
                  toast.success(`Goal for Class of ${y} set to ${target}%`);
                  setTkKey((k) => k + 1);
                } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
                finally { setSavingTarget(false); }
              }}
              className="px-2.5 py-1 rounded-md bg-secondary text-white text-xs font-medium hover:brightness-110 disabled:opacity-50">
              {savingTarget ? "Saving…" : "Save goal"}
            </button>
            <button data-testid="open-toolbox" onClick={() => { localStorage.setItem("ud_toolkit_expanded", "1"); setTkKey((k) => k + 1); }}
              className="ml-1 flex items-center gap-1.5 px-3 py-1 rounded-md bg-primary text-white text-xs font-medium hover:bg-primary/90 normal-case tracking-normal transition-colors">
              <Maximize2 className="h-3.5 w-3.5" /> Open Class Agent Toolbox
            </button>
          </div>
          {(previewYear || byYear[0]?.[0]) && (
            <AgentToolkit key={`${previewYear || byYear[0]?.[0]}-${tkKey}`} classYear={previewYear || byYear[0]?.[0]} onData={(dd) => setTarget(dd.goal.target)} />
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : agents.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <UserCog className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <div className="font-serif text-xl">No Class Agents yet</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Designate an alumnus as a Class Agent from a class roster. They must have a claimed login to be assigned.
          </p>
          <Link to="/classes" className="inline-block mt-4 px-4 py-2 rounded-md border border-border text-sm hover:bg-muted">Go to Classes</Link>
        </div>
      ) : (
      <>
        {/* Filter bar — search + class filter for the agent roster below */}
        <div data-testid="agents-filter-bar" className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="agents-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search agents by name, email, city…"
              className="w-full rounded-md border border-input bg-background pl-9 pr-9 py-2 text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
            {search && (
              <button data-testid="agents-search-clear" onClick={() => setSearch("")} title="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted text-muted-foreground"><X className="h-4 w-4" /></button>
            )}
          </div>
          <div className="relative">
            <button data-testid="agents-class-filter" onClick={() => setClassMenuOpen((o) => !o)}
              className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-muted focus:ring-2 focus:ring-accent focus:outline-none">
              <span>{selectedClasses.length === 0 ? `All classes (${agents.length})` : `${selectedClasses.length} class${selectedClasses.length === 1 ? "" : "es"} selected`}</span>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${classMenuOpen ? "rotate-180" : ""}`} />
            </button>
            {classMenuOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setClassMenuOpen(false)} />
                <div data-testid="agents-class-menu" className="absolute z-40 mt-1 left-0 min-w-[220px] max-h-72 overflow-y-auto rounded-md border border-border bg-card shadow-lg p-1">
                  <button data-testid="agents-class-all" onClick={() => setSelectedClasses([])}
                    className="w-full flex items-center gap-2 px-2.5 py-2 rounded text-sm text-left hover:bg-muted">
                    <span className={`h-4 w-4 rounded-sm border flex items-center justify-center ${selectedClasses.length === 0 ? "bg-primary border-primary text-white" : "border-input"}`}>{selectedClasses.length === 0 && <Check className="h-3 w-3" />}</span>
                    <span className="flex-1">All classes</span>
                    <span className="text-xs text-muted-foreground">{agents.length}</span>
                  </button>
                  <div className="my-1 border-t border-border" />
                  {byYear.map(([y, list]) => {
                    const on = selectedClasses.includes(String(y));
                    return (
                      <button key={y} data-testid={`agents-class-opt-${y}`}
                        onClick={() => setSelectedClasses((prev) => on ? prev.filter((x) => x !== String(y)) : [...prev, String(y)])}
                        className="w-full flex items-center gap-2 px-2.5 py-2 rounded text-sm text-left hover:bg-muted">
                        <span className={`h-4 w-4 rounded-sm border flex items-center justify-center ${on ? "bg-primary border-primary text-white" : "border-input"}`}>{on && <Check className="h-3 w-3" />}</span>
                        <span className="flex-1">Class of {y}</span>
                        <span className="text-xs text-muted-foreground">{list.length}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          {(search || selectedClasses.length > 0) && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span data-testid="agents-filter-count">{filteredAgents.length} of {agents.length}</span>
              <button data-testid="agents-filter-reset" onClick={() => { setSearch(""); setSelectedClasses([]); }}
                className="text-xs px-2 py-1 rounded border border-border hover:bg-muted">Clear filters</button>
            </div>
          )}
          <div className="flex rounded-md border border-border overflow-hidden ml-auto" title="Change how agents are displayed below">
            <button data-testid="agents-view-grid" onClick={() => setViewPersist("grid")} className={`flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium ${view === "grid" ? "bg-secondary text-white" : "hover:bg-muted text-muted-foreground"}`} title="Grid view"><LayoutGrid className="h-4 w-4" /> Grid</button>
            <button data-testid="agents-view-list" onClick={() => setViewPersist("list")} className={`flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium border-l border-border ${view === "list" ? "bg-secondary text-white" : "hover:bg-muted text-muted-foreground"}`} title="List view"><ListIcon className="h-4 w-4" /> List</button>
          </div>
        </div>

        {filteredAgents.length === 0 ? (
          <div data-testid="agents-no-matches" className="bg-card border border-border rounded-lg p-10 text-center text-sm text-muted-foreground">
            No Class Agents match your filters.
          </div>
        ) : view === "list" ? (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-mono uppercase tracking-wide text-muted-foreground border-b border-border">
                <th className="px-4 py-2.5 font-medium">Agent</th>
                <th className="px-4 py-2.5 font-medium">Class</th>
                <th className="px-4 py-2.5 font-medium">Email</th>
                <th className="px-4 py-2.5 font-medium">Phone</th>
                <th className="px-4 py-2.5 font-medium">Location</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgents.map((a) => (
                <tr key={a.id} data-testid={`agent-row-${a.id}`} className="border-b border-border last:border-0 hover:bg-muted/40">
                  <td className="px-4 py-2.5">
                    <Link to={`/alumni/${a.id}`} className="flex items-center gap-2 font-medium hover:text-primary">
                      <ShieldCheck className="h-4 w-4 text-accent shrink-0" /> {a.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{a.class_year || "—"}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {a.email
                      ? <span className="inline-flex items-center gap-1.5">{a.email}{a.email_opt_out && <span className="text-[10px] font-mono uppercase text-amber-600">opted out</span>}</span>
                      : <span className="text-amber-600">No email</span>}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{a.phone ? formatPhone(a.phone) : "—"}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{[a.city, a.state].filter(Boolean).join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-8">
          {shownByYear.map(([year, list]) => (
            <div key={year} data-testid={`agent-group-${year}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="font-serif text-xl tracking-tight">Class of {year}</span>
                <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{list.length}</span>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {list.map((a) => (
                  <div key={a.id} data-testid={`agent-card-${a.id}`} className="bg-card border border-border rounded-lg p-4 flex items-start gap-3">
                    <div className="h-11 w-11 rounded-full bg-accent/20 text-secondary flex items-center justify-center shrink-0">
                      <ShieldCheck className="h-5 w-5 text-accent" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Link to={`/alumni/${a.id}`} className="font-medium hover:text-primary truncate block">{a.name}</Link>
                      <div className="mt-1.5 space-y-1 text-[13px] text-muted-foreground">
                        {a.email
                          ? <div className="flex items-center gap-1.5 truncate"><Mail className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{a.email}</span>{a.email_opt_out && <span className="text-[10px] font-mono uppercase text-amber-600">opted out</span>}</div>
                          : <div className="flex items-center gap-1.5 text-amber-600"><Mail className="h-3.5 w-3.5 shrink-0" /> No email on file</div>}
                        {a.phone && <div className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 shrink-0" /> {formatPhone(a.phone)}</div>}
                        {(a.city || a.state) && <div className="flex items-center gap-1.5 truncate"><MapPin className="h-3.5 w-3.5 shrink-0" /> {[a.city, a.state].filter(Boolean).join(", ")}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {compose && <ComposeModal count={emailable} onClose={() => setCompose(false)} />}
    </div>
  );
}

const Stat = ({ label, value, icon: Icon }) => (
  <div className="flex items-center gap-3 bg-card border border-border rounded-lg px-4 py-3">
    <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-primary" /></div>
    <div>
      <div className="text-xl font-semibold leading-none">{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground mt-1">{label}</div>
    </div>
  </div>
);

const ComposeModal = ({ count, onClose }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("Dear {first_name},\n\n");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/class-agents/email", { subject, body });
      toast.success(`Sent to ${data.sent} Class Agent${data.sent === 1 ? "" : "s"}${data.failed ? ` · ${data.failed} failed` : ""}`);
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return createPortal((
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/60 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-card w-full max-w-lg rounded-lg shadow-2xl my-8" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-primary" /><span className="font-serif text-lg">Message All Class Agents</span></div>
          <button data-testid="compose-close" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
            <Users className="h-4 w-4" /> Sending to <b className="text-foreground">{count}</b> opted-in agent{count === 1 ? "" : "s"}.
          </div>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Subject</span>
            <input data-testid="compose-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="A message for our Class Agents" className="ud-input mt-1" />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Message</span>
            <textarea data-testid="compose-body" value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="ud-input mt-1 font-sans" />
            <span className="text-[11px] text-muted-foreground mt-1 block">Use <code>{"{first_name}"}</code> to personalize. An unsubscribe link is added automatically.</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted">Cancel</button>
          <button data-testid="compose-send" onClick={send} disabled={busy || !count} className="flex items-center gap-2 px-4 py-2 rounded-md bg-secondary text-white text-sm font-medium hover:brightness-110 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to Agents
          </button>
        </div>
      </div>
    </div>
  ), document.body);
};
